import { z } from "zod"

const transcript = z.object({
    type: z.enum(["session.input_transcript.delta", "session.output_transcript.delta"]),
    event_id: z.string().optional(),
    delta: z.string(),
    start_ms: z.number(),
    end_ms: z.number(),
})
const delegation = z.object({ type: z.literal("session.delegation.created"), delegation: z.object({ id: z.string(), target: z.literal("client") }) })
export interface TranscriptFragment {
    speaker: "user" | "assistant"
    text: string
    start: number
    end: number
    sequence: number
}
export interface VoiceWork {
    delegationId: string
    requestId: string
    revision: number
    input: string
    userSpeech: string
}

/** Timeline fragments remain data; only a delegation event can execute work. */
export class VoiceProtocol {
    fragments: TranscriptFragment[] = []
    revision = 0
    private sequence = 0
    private consumed = 0
    private inputContext: unknown = undefined
    private seen = new Set<string>()
    private pending = new Set<string>()

    accept(value: unknown, context?: () => unknown): { transcript?: TranscriptFragment; delegationId?: string } {
        const text = transcript.safeParse(value)
        if (text.success) {
            const event = text.data
            if (event.event_id && this.seen.has(event.event_id)) return {}
            if (event.event_id) this.remember(event.event_id)
            const fragment: TranscriptFragment = {
                speaker: event.type === "session.input_transcript.delta" ? "user" : "assistant",
                text: event.delta,
                start: event.start_ms,
                end: event.end_ms,
                sequence: ++this.sequence,
            }
            if (fragment.speaker === "user") {
                if (this.inputContext === undefined) this.inputContext = context?.()
                this.revision += 1
            }
            this.fragments.push(fragment)
            if (this.fragments.length > 2000) this.fragments.shift()
            return { transcript: fragment }
        }
        const request = delegation.safeParse(value)
        if (request.success && !this.pending.has(request.data.delegation.id)) {
            this.pending.add(request.data.delegation.id)
            return { delegationId: request.data.delegation.id }
        }
        return {}
    }

    prepare(delegationId: string, context: unknown): VoiceWork | undefined {
        const newInput = this.fragments.filter((fragment) => fragment.speaker === "user" && fragment.sequence > this.consumed)
        if (newInput.length === 0) return undefined
        this.consumed = this.sequence
        const inputContext = this.inputContext ?? context
        this.inputContext = undefined
        return {
            delegationId,
            requestId: crypto.randomUUID(),
            revision: this.revision,
            userSpeech: newInput.map((fragment) => fragment.text).join(""),
            input: JSON.stringify({
                latestUserSpeech: newInput
                    .map((fragment) => fragment.text)
                    .join("")
                    .slice(-16000),
                recentConversation: this.fragments.slice(-80).map(({ speaker, text, start, end }) => ({ speaker, text, start, end })),
                clientContext: inputContext,
                currentClientContext: JSON.stringify(inputContext) === JSON.stringify(context) ? "unchanged" : context,
            }),
        }
    }
    private remember(id: string) {
        this.seen.add(id)
        if (this.seen.size > 4000) {
            const oldest = this.seen.values().next().value
            if (oldest) this.seen.delete(oldest)
        }
    }
}
